import React from 'react';
import { Card, Tag, Typography, Image, Row, Col, Collapse } from 'antd';
import {
  RobotOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  PictureOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { formatCost, formatTokens } from '../services';
import { queryModeLabel } from '../models';
import type { AdvancedSearchResponse } from '../models';

const { Text, Paragraph } = Typography;

interface AnswerCardProps {
  result: AdvancedSearchResponse;
}

const AnswerCard: React.FC<AnswerCardProps> = ({ result }) => {
  const mode = queryModeLabel[result.query_mode] || {
    text: result.query_mode,
    color: 'default',
  };

  const images = result.related_images || [];

  return (
    <Card
      className="mb-4 shadow-sm"
      style={{ borderLeft: '4px solid #1677ff' }}
      title={
        <div className="flex items-center justify-between w-full">
          <span className="flex items-center gap-2 text-blue-600">
            <RobotOutlined />
            AI 回答
          </span>
          <div className="flex items-center gap-2">
            <Tag color={mode.color} icon={<ThunderboltOutlined />}>
              {mode.text}
            </Tag>
            {result.total_results > 0 && (
              <Tag color="cyan">共 {result.total_results} 筆結果</Tag>
            )}
          </div>
        </div>
      }
    >
      <Paragraph
        style={{
          whiteSpace: 'pre-wrap',
          fontSize: '15px',
          lineHeight: '1.75',
          color: '#1f2937',
          marginBottom: 0,
        }}
      >
        {result.answer}
      </Paragraph>

      {/* Related images from file-sourced knowledge */}
      {images.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <Text type="secondary" className="text-xs flex items-center gap-1 mb-2">
            <PictureOutlined />
            相關圖片 ({images.length})
            {images[0].source_file && (
              <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>
                {images[0].source_file}
              </Tag>
            )}
          </Text>
          <Image.PreviewGroup>
            <Row gutter={[8, 8]}>
              {images.map((img) => (
                <Col key={img.id} xs={12} sm={8} md={6}>
                  <div style={{ borderRadius: 6, overflow: 'hidden', border: '1px solid #f0f0f0' }}>
                    <Image
                      src={img.gcs_url}
                      alt={img.description || `Page ${img.page_number}`}
                      style={{ objectFit: 'contain', maxHeight: 140, width: '100%' }}
                      fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y1ZjVmNSIvPjwvc3ZnPg=="
                    />
                    <div style={{ padding: '4px 6px', fontSize: 11, color: '#8c8c8c' }}>
                      p.{img.page_number}
                    </div>
                  </div>
                  {img.ocr_text && (
                    <Collapse
                      ghost
                      size="small"
                      items={[{
                        key: '1',
                        label: <Text type="secondary" style={{ fontSize: 11 }}><FileTextOutlined /> 圖片描述</Text>,
                        children: <Paragraph style={{ fontSize: 11, margin: 0 }} ellipsis={{ rows: 3, expandable: true }}>{img.ocr_text}</Paragraph>,
                      }]}
                    />
                  )}
                </Col>
              ))}
            </Row>
          </Image.PreviewGroup>
        </div>
      )}

      {(result.total_tokens > 0 || result.estimated_cost > 0) && (
        <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap gap-4">
          {result.total_tokens > 0 && (
            <Text type="secondary" className="text-xs flex items-center gap-1">
              <DollarOutlined />
              Token 使用：{formatTokens(result.total_tokens)}
            </Text>
          )}
          {result.estimated_cost > 0 && (
            <Text type="secondary" className="text-xs flex items-center gap-1">
              <DollarOutlined />
              估計費用：{formatCost(result.estimated_cost)} USD
            </Text>
          )}
          {result.processing_ms > 0 && (
            <Text type="secondary" className="text-xs flex items-center gap-1">
              <ClockCircleOutlined />
              處理時間：{(result.processing_ms / 1000).toFixed(1)}s
            </Text>
          )}
        </div>
      )}
    </Card>
  );
};

export default AnswerCard;
